// =================================================================
// Cognitive Assessment — data and scoring
//
// Ported from games.quiltt.com/cognitive-assessment/. The structure
// is preserved so the clinical content is identical; only the
// rendering layer differs (React vs. vanilla JS).
//
// Screen types:
//   welcome    — intro, no scoring
//   mcq        — multiple choice (chosen === correctIndex)
//   encode     — timed display, no scoring (sets up later questions)
//   reaction   — reaction-time test (Stage 2): several trials, the
//                average ms is bucketed into points. Stores raw ms.
//   text-entry — free-text answer (Stage 2): lenient matching against
//                an accepted-answer list.
//   results    — triggers save + navigation
//
// Scoring totals have evolved:
//   v1 (original):     26 points, 13 MCQ items
//   Stage 1 (recall):  32 points, +3 recall-trivia MCQs
//   Stage 2 (this):    38 points, +1 reaction (4 pts) +1 verbal (2 pts)
//
// Score → level mapping comes from the catalog (levelForScore).
// The level THRESHOLDS are interim placeholders pending Dr.
// Winningham — see LEVELS in gamesCatalog.js.
// =================================================================

import { LEVELS, levelForScore } from '../data/gamesCatalog.js';

// -----------------------------------------------------------------
// Date/day option builders (used in Q1 and Q2)
// -----------------------------------------------------------------
// The correct date and day are computed at runtime from "today",
// with distractors offset by ±1 or ±2 days.

const DAYS_OF_WEEK = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday',
  'Thursday', 'Friday', 'Saturday',
];

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/**
 * Build the four date options for Q1.
 * Offsets are [-2, -1, 0, +1] days, with the correct answer at index 2.
 */
export function buildDateOptions(today = new Date()) {
  const offsets = [-2, -1, 0, +1];
  return offsets.map((days) => {
    const d = new Date(today);
    d.setDate(d.getDate() + days);
    return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  });
}

/**
 * Build the day-of-week options for Q2.
 * Days are [yesterday, today, tomorrow, +2], with correct at index 1.
 */
export function buildDayOptions(today = new Date()) {
  const todayIdx = today.getDay();
  return [-1, 0, 1, 2].map((offset) => {
    const idx = (todayIdx + offset + 7) % 7;
    return DAYS_OF_WEEK[idx];
  });
}


// -----------------------------------------------------------------
// Reaction-time scoring (Stage 2)
// -----------------------------------------------------------------
// The reaction screen runs several trials; each valid trial yields a
// millisecond latency. We average the valid trials and bucket the
// average into points.
//
// !!! PENDING DR. WINNINGHAM — INTERIM BUCKETS !!!
// These ms cut points are reasonable defaults for an older adult
// population but are NOT clinically validated. Dr. Rob may adjust.
// This is the only place reaction scoring lives.
// -----------------------------------------------------------------

export const REACTION_MAX_POINTS = 4;

// [maxMs, points] — first bucket whose maxMs the average is <= wins.
const REACTION_BUCKETS = [
  [350, 4],
  [450, 3],
  [600, 2],
  [800, 1],
  [Infinity, 0],
];

/**
 * Average the valid reaction trials (ms) and bucket into points.
 * @param {number[]} trialsMs - latencies in ms for VALID trials only
 * @returns {{ averageMs: number|null, points: number }}
 */
export function scoreReaction(trialsMs) {
  const valid = (trialsMs || []).filter((n) => typeof n === 'number' && n > 0);
  if (valid.length === 0) {
    return { averageMs: null, points: 0 };
  }
  const averageMs = Math.round(valid.reduce((a, b) => a + b, 0) / valid.length);
  const bucket = REACTION_BUCKETS.find(([maxMs]) => averageMs <= maxMs);
  return { averageMs, points: bucket ? bucket[1] : 0 };
}

// -----------------------------------------------------------------
// Lenient text matching (Stage 2)
// -----------------------------------------------------------------
// For the name-the-animal item. We accept case/whitespace variation,
// a small list of accepted spellings/synonyms per answer, and close
// misspellings within a small edit distance scaled to word length.
// -----------------------------------------------------------------

function levenshtein(a, b) {
  const m = a.length, n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  let prev = Array.from({ length: n + 1 }, (_, i) => i);
  let curr = new Array(n + 1);
  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
    }
    [prev, curr] = [curr, prev];
  }
  return prev[n];
}

/**
 * Does a typed answer leniently match any accepted answer?
 * @param {string} input - what the user typed
 * @param {string[]} accepted - accepted spellings/synonyms (lowercase)
 * @returns {boolean}
 */
export function matchesAnswer(input, accepted) {
  const cleaned = (input || '').trim().toLowerCase().replace(/\s+/g, ' ');
  if (!cleaned) return false;
  for (const ans of accepted) {
    if (cleaned === ans) return true;
    // Allow a small edit distance for longer words (typos), scaled so
    // short words must be near-exact. e.g. "flamingo" tolerates 2,
    // "zebra" tolerates 1.
    const tolerance = ans.length >= 7 ? 2 : ans.length >= 5 ? 1 : 0;
    if (tolerance > 0 && levenshtein(cleaned, ans) <= tolerance) return true;
  }
  return false;
}

// -----------------------------------------------------------------
// Animal pool for the verbal name-the-animal item (Stage 2)
// -----------------------------------------------------------------
// One is chosen at random per assessment (pickAnimal). Each has an
// SVG key (the React layer renders the matching line-art) and a list
// of accepted lowercase answers for lenient matching.
// -----------------------------------------------------------------

export const ANIMALS = [
  { key: 'flamingo', accepted: ['flamingo', 'flamingos', 'flamengo'] },
  { key: 'zebra', accepted: ['zebra', 'zebras'] },
  { key: 'hippo', accepted: ['hippo', 'hippos', 'hippopotamus', 'hippopotamous'] },
];

/**
 * Pick one animal for this assessment run. Deterministic if a seed
 * index is supplied (useful for testing/preview); random otherwise.
 */
export function pickAnimal(seedIndex) {
  if (typeof seedIndex === 'number') {
    return ANIMALS[seedIndex % ANIMALS.length];
  }
  return ANIMALS[Math.floor(Math.random() * ANIMALS.length)];
}


// -----------------------------------------------------------------
// Assessment screens
// -----------------------------------------------------------------
// Each screen is one of: welcome | mcq | encode | reaction |
// text-entry | results
// - mcq screens have `points` and `correctIndex` for scoring
// - encode screens display content for a fixed duration, no scoring
// - reaction screens have `points` and `trials`; scored via scoreReaction
// - text-entry screens have `points` and an animal (accepted answers)
//
// The `domain` field maps each scored question to a cognitive
// domain. This lets us record per-domain points so the recommendation
// engine can surface weak domains later.
// -----------------------------------------------------------------

export const ASSESSMENT_SCREENS = [
  { type: 'welcome' },

  // ---- Q1: Date orientation ----
  {
    type: 'mcq',
    qNum: 1,
    domain: 'executive',
    prompt: "What is today's date?",
    optionsFn: buildDateOptions,
    correctIndex: 2,
    points: 2,
  },

  // ---- Q2: Day of week ----
  {
    type: 'mcq',
    qNum: 2,
    domain: 'executive',
    prompt: "What is today's day of the week?",
    optionsFn: buildDayOptions,
    correctIndex: 1,
    points: 2,
  },

  // ---- Encode: 5 words for 8 seconds ----
  {
    type: 'encode',
    variant: 'words',
    instruction: 'Please remember these five words',
    words: ['Chair', 'Dog', 'Coat', 'Cake', 'Rock'],
    duration: 8,
  },

  // ---- Encode: math problem for 10 seconds ----
  {
    type: 'encode',
    variant: 'math',
    instruction: 'Read the following carefully',
    duration: 10,
  },

  // ---- Q3: Spend amount ----
  {
    type: 'mcq',
    qNum: 3,
    domain: 'executive',
    prompt: 'How much did you spend?',
    options: ['$55', '$57', '$14', '$12', "I don't know"],
    correctIndex: 3,
    points: 2,
  },

  // ---- Q4: Remaining amount ----
  {
    type: 'mcq',
    qNum: 4,
    domain: 'executive',
    prompt: 'How much do you have left?',
    options: ['$38', '$43', '$45', '$62', "I don't know"],
    correctIndex: 0,
    points: 3,
  },

  // ---- Q5: Word recall — NOT a word ----
  {
    type: 'mcq',
    qNum: 5,
    domain: 'encoding',
    prompt: 'Which of the following was NOT one of the five words you read earlier?',
    options: ['Cake', 'Rock', 'Cat', 'Chair'],
    correctIndex: 2,
    points: 2,
  },

  // ---- Q6: Word recall — NOT a word ----
  {
    type: 'mcq',
    qNum: 6,
    domain: 'encoding',
    prompt: 'Once again, which of the following was NOT one of the five words you read earlier?',
    options: ['Dog', 'Table', 'Coat', 'Chair'],
    correctIndex: 1,
    points: 2,
  },

  // ---- Q7: Time +1 hour ----
  {
    type: 'mcq',
    qNum: 7,
    domain: 'executive',
    showClock: true,
    context: 'Suppose the time is currently 10:10.',
    prompt: 'What time will it be in one hour?',
    options: ['9:10', '10:10', '11:10', '12:10'],
    correctIndex: 2,
    points: 1,
  },

  // ---- Q8: Time +70 minutes ----
  {
    type: 'mcq',
    qNum: 8,
    domain: 'executive',
    showClock: true,
    context: 'Suppose the time is currently 10:10.',
    prompt: 'What time will it be in 70 minutes?',
    options: ['10:20', '11:10', '11:20', '12:10'],
    correctIndex: 2,
    points: 2,
  },

  // ---- Q9: Math 22+1+12 ----
  {
    type: 'mcq',
    qNum: 9,
    domain: 'speed',
    context: '22 + 1 + 12',
    prompt: 'What is the correct answer?',
    options: ['34', '35', '36', '37'],
    correctIndex: 1,
    points: 2,
  },

  // ---- Q10: Math 22-3-12 ----
  {
    type: 'mcq',
    qNum: 10,
    domain: 'speed',
    context: '22 − 3 − 12',
    prompt: 'What is the correct answer?',
    options: ['5', '6', '7', '8'],
    correctIndex: 2,
    points: 2,
  },

  // ---- Reaction time (Stage 2) ----
  // Speed domain. Several trials; tap when the screen turns yellow.
  // Average latency is bucketed into points by scoreReaction().
  {
    type: 'reaction',
    qNum: 'R',
    domain: 'speed',
    trials: 5,
    points: 4,
    instruction: 'When the screen turns yellow, tap as fast as you can.',
  },

  // ---- Encode: story for 30 seconds (skippable) ----
  {
    type: 'encode',
    variant: 'story',
    instruction: 'Please read the following story carefully',
    duration: 30,
    skippable: true,
    story:
      'A 12-year-old girl walked her dog to the park, where they ' +
      'played fetch with a tennis ball. It was a warm spring day ' +
      'and there were no clouds in the sky. While at the park, ' +
      'the girl saw one of her former teachers, who stopped to ' +
      'say hi and petted her dog.',
  },

  // ---- Q11: Girl's age ----
  {
    type: 'mcq',
    qNum: 11,
    domain: 'encoding',
    prompt: 'How old was the girl?',
    options: ['9', '10', '11', '12'],
    correctIndex: 3,
    points: 2,
  },

  // ---- Q12: Where ----
  {
    type: 'mcq',
    qNum: 12,
    domain: 'encoding',
    prompt: 'Where did the girl walk her dog to?',
    options: ['Park', 'School', 'Home', 'Store'],
    correctIndex: 0,
    points: 2,
  },

  // ---- Q13: Who ----
  {
    type: 'mcq',
    qNum: 13,
    domain: 'encoding',
    prompt: 'Who did the girl see while she was out?',
    options: ['Neighbor', 'Friend', 'Former teacher', 'Her brother'],
    correctIndex: 2,
    points: 2,
  },

  // ---- Long-term recall (trivia) ----
  // Added per Dr. Winningham to assess the "Remembering the Past"
  // (recall) domain — long-known general knowledge, as distinct from
  // the encoding items above (which test material learned during the
  // assessment itself). These are intentionally simple, broadly
  // familiar facts, not obscure trivia.

  // ---- Q14: NYC nickname ----
  {
    type: 'mcq',
    qNum: 14,
    domain: 'recall',
    prompt: 'What is the nickname for New York City?',
    options: ['The Windy City', 'The Big Apple', 'The Motor City', 'The Emerald City'],
    correctIndex: 1,
    points: 2,
  },

  // ---- Q15: Statue of Liberty ----
  {
    type: 'mcq',
    qNum: 15,
    domain: 'recall',
    prompt: 'Which country gifted the Statue of Liberty to the United States?',
    options: ['England', 'Italy', 'France', 'Spain'],
    correctIndex: 2,
    points: 2,
  },

  // ---- Name the animal (Stage 2) ----
  // Verbal domain. Shows a line-art animal; the resident types its
  // name. Lenient matching via matchesAnswer(). The specific animal
  // is chosen at runtime by the React layer (pickAnimal) and attached
  // to this screen as `animal`, so accepted answers travel with it.
  {
    type: 'text-entry',
    qNum: 'V',
    domain: 'verbal',
    points: 2,
    prompt: 'What is the name of this animal?',
    placeholder: 'Type the animal\u2019s name',
  },

  { type: 'results' },
];

// Screen types that carry points and count toward the score.
export const SCORED_TYPES = ['mcq', 'reaction', 'text-entry'];

// Number of scored questions (used for progress display).
export const TOTAL_QUESTIONS = ASSESSMENT_SCREENS.filter(
  (s) => SCORED_TYPES.includes(s.type)
).length;

// Total possible points across all scored questions.
export const TOTAL_POINTS = ASSESSMENT_SCREENS
  .filter((s) => SCORED_TYPES.includes(s.type))
  .reduce((sum, s) => sum + (s.points || 0), 0);

// -----------------------------------------------------------------
// Scoring helpers
// -----------------------------------------------------------------

/**
 * Given the running per-question answers, compute final score, level,
 * and per-domain breakdown.
 *
 * @param {Array<{qNum, domain, earned, possible}>} answers
 * @returns {{score, totalPossible, level, pointsByDomain}}
 */
export function computeResults(answers) {
  const score = answers.reduce((sum, a) => sum + a.earned, 0);
  const totalPossible = TOTAL_POINTS;
  const level = levelForScore(score);

  // Per-domain totals: { verbal: { earned: 0, possible: 0 }, ... }
  const pointsByDomain = {};
  answers.forEach(({ domain, earned, possible }) => {
    if (!domain) return;
    if (!pointsByDomain[domain]) {
      pointsByDomain[domain] = { earned: 0, possible: 0 };
    }
    pointsByDomain[domain].earned += earned;
    pointsByDomain[domain].possible += possible;
  });

  return { score, totalPossible, level, pointsByDomain };
}

/**
 * Friendly descriptions of each level. Shown to the resident on the
 * results screen. Tied to Dr. Rob's framing — these are placeholders
 * to be reviewed/revised by him.
 */
export const LEVEL_DESCRIPTIONS = {
  brisk:
    'Strong cognitive performance across all domains. ' +
    "You'll do well with our most challenging brain health activities.",
  standard:
    'Solid cognitive performance overall. ' +
    "You'll be matched with moderately challenging brain health activities.",
  supported:
    "You'll be best supported by activities pitched at an accessible " +
    'difficulty, with extra time and gentle prompts.',
  guided:
    "You'll be best supported by simpler, calming activities with " +
    'shorter sessions and lots of encouragement — often best done ' +
    'together with staff or family.',
};

// Re-export so consumers can import from one place.
export { LEVELS };
